<script setup>
import { reactive, computed, watch, getCurrentInstance, ref } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(
  () => $stores.appData?.state?.data?.addresses || {}
);

// Get country codes from source data keys
const countryCodes = computed(() =>
  Object.keys(sourceData.value).filter((code) => sourceData.value[code])
);

const form = reactive({});

// Initialize form for each country
watch(
  sourceData,
  (data) => {
    if (!data) return;
    Object.keys(data).forEach((code) => {
      if (data[code] && !form[code]) {
        form[code] = {
          usdtTrc20Address: '',
          usdtErc20Address: '',
          usdtBep20Address: '',
          binanceAccountId: '',
          betwinnerWithdrawAddress: '',
          melbetWithdrawAddress: '',
          eightStarzWithdrawAddress: '',
          oneWinWithdrawAddress: '',
          xbetWithdrawAddress: ''
        };
      }
      if (data[code] && form[code]) {
        Object.assign(form[code], {
          usdtTrc20Address: data[code].usdtTrc20Address || '',
          usdtErc20Address: data[code].usdtErc20Address || '',
          usdtBep20Address: data[code].usdtBep20Address || '',
          binanceAccountId: data[code].binanceAccountId || '',
          betwinnerWithdrawAddress: data[code].betwinnerWithdrawAddress || '',
          melbetWithdrawAddress: data[code].melbetWithdrawAddress || '',
          eightStarzWithdrawAddress: data[code].eightStarzWithdrawAddress || '',
          oneWinWithdrawAddress: data[code].oneWinWithdrawAddress || '',
          xbetWithdrawAddress: data[code].xbetWithdrawAddress || ''
        });
      }
    });
  },
  { immediate: true, deep: true }
);

const selectedCountry = ref(countryCodes.value[0] || '');

const hasChanges = computed(() => {
  return countryCodes.value.some((code) =>
    Object.keys(form[code] || {}).some(
      (key) => form[code]?.[key] !== sourceData.value?.[code]?.[key]
    )
  );
});

const selectedCountryHasChanges = computed(() =>
  Object.keys(form[selectedCountry.value] || {}).some(
    (key) =>
      form[selectedCountry.value]?.[key] !==
      sourceData.value?.[selectedCountry.value]?.[key]
  )
);

const selectedCountryData = computed(() => {
  const country = Countries.find(selectedCountry.value);
  return {
    name: country?.countryName || selectedCountry.value,
    code: selectedCountry.value
  };
});

function save() {
  if (!hasChanges.value) return;

  const payload = { addresses: {} };

  countryCodes.value.forEach((code) => {
    payload.addresses[code] = {};
    Object.keys(form[code] || {}).forEach((key) => {
      if (form[code][key] !== sourceData.value?.[code]?.[key]) {
        payload.addresses[code][key] = form[code][key];
      }
    });
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_ADDRESSES, payload);
}

function reset() {
  countryCodes.value.forEach((code) => {
    if (sourceData.value?.[code] && form[code]) {
      Object.assign(form[code], sourceData.value[code]);
    }
  });
}

function resetCountry(code) {
  if (sourceData.value?.[code] && form[code]) {
    Object.assign(form[code], sourceData.value[code]);
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center">
      <h2 class="text-xl font-semibold">Adresses de retrait par pays</h2>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="reset"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- COUNTRY SELECTOR -->
    <div class="flex flex-wrap gap-2">
      <button
        v-for="code in countryCodes"
        :key="code"
        @click="selectedCountry = code"
        :class="[
          'px-4 py-2 rounded-lg font-medium transition-all',
          selectedCountry === code
            ? 'bg-primary-600 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        ]"
      >
        {{ Countries.find(code)?.countryName || code }}
      </button>
    </div>

    <!-- COUNTRY DETAILS -->
    <div class="bg-slate-100 rounded-3xl p-6">
      <div
        class="flex justify-between items-center mb-6 pb-3 border-b border-gray-300"
      >
        <h3 class="text-lg font-semibold">
          {{ selectedCountryData.name }} ({{ selectedCountryData.code }})
        </h3>
        <button
          v-if="selectedCountryHasChanges"
          class="px-4 py-2 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetCountry(selectedCountry)"
        >
          Annuler
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <!-- USDT ADDRESSES -->
        <div class="lg:col-span-3">
          <h4 class="font-semibold mb-3 text-primary-600">Adresses USDT</h4>
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium">TRC20</label>
          <input
            class="inputComponent"
            v-model="form[selectedCountry].usdtTrc20Address"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium">ERC20</label>
          <input
            class="inputComponent"
            v-model="form[selectedCountry].usdtErc20Address"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium">BEP20</label>
          <input
            class="inputComponent"
            v-model="form[selectedCountry].usdtBep20Address"
          />
        </div>

        <!-- BINANCE -->
        <div class="lg:col-span-3 mt-2">
          <h4 class="font-semibold mb-3 text-primary-600">Binance</h4>
        </div>

        <div class="lg:col-span-3">
          <label class="block mb-1 text-sm font-medium"
            >Binance Account ID</label
          >
          <input
            class="inputComponent"
            v-model="form[selectedCountry].binanceAccountId"
          />
        </div>

        <!-- BETTING WITHDRAWAL ADDRESSES -->
        <div class="lg:col-span-3 mt-2">
          <h4 class="font-semibold mb-3 text-primary-600">
            Adresses de retrait (Betting)
          </h4>
        </div>

        <div class="lg:col-span-3">
          <label class="block mb-1 text-sm font-medium">Betwinner</label>
          <textarea
            class="inputComponent w-full h-20 resize-none"
            v-model="form[selectedCountry].betwinnerWithdrawAddress"
            placeholder="Une adresse par ligne..."
          ></textarea>
        </div>

        <div class="lg:col-span-3">
          <label class="block mb-1 text-sm font-medium">Melbet</label>
          <textarea
            class="inputComponent w-full h-20 resize-none"
            v-model="form[selectedCountry].melbetWithdrawAddress"
            placeholder="Une adresse par ligne..."
          ></textarea>
        </div>

        <div class="lg:col-span-3">
          <label class="block mb-1 text-sm font-medium">8 Starz</label>
          <textarea
            class="inputComponent w-full h-20 resize-none"
            v-model="form[selectedCountry].eightStarzWithdrawAddress"
            placeholder="Une adresse par ligne..."
          ></textarea>
        </div>

        <div class="lg:col-span-3">
          <label class="block mb-1 text-sm font-medium">1Win</label>
          <textarea
            class="inputComponent w-full h-20 resize-none"
            v-model="form[selectedCountry].oneWinWithdrawAddress"
            placeholder="Une adresse par ligne..."
          ></textarea>
        </div>

        <div class="lg:col-span-3">
          <label class="block mb-1 text-sm font-medium">1xBet</label>
          <textarea
            class="inputComponent w-full h-20 resize-none"
            v-model="form[selectedCountry].xbetWithdrawAddress"
            placeholder="Une adresse par ligne..."
          ></textarea>
        </div>
      </div>
    </div>
  </div>
</template>
